import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DepartmentsService } from "@root/services/departments";
import { useNotification } from "./useNotification";
import { PAGINATION_DEFAULTS } from "@root/constants";

export function useDepartments() {
  const [departmentData, setDepartmentData] = useState<any>([]);
  const [departmentCount, setDepartmentCount] = useState<number>(0);
  const [departmentHasMore, setDepartmentHasMore] = useState<boolean>(true);
  const [addDepartmentLoading, setAddDepartmentLoading] = useState(false);
  const [updateDepartmentLoading, setUpdateDepartmentLoading] = useState(false);
  const [deleteDepartmentLoading, setDeleteDepartmentLoading] = useState(false);
  const [getDepartmentsLoading, setGetDepartmentsLoading] = useState(false);
  const [getDepartmentLoading, setGetDepartmentLoading] = useState(false);

  const departmentsService = new DepartmentsService();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");

  const fetchDepartments = async (payload: {
    searchQuery?: string;
    offset?: number;
    limit?: number;
    append?: boolean;
  }) => {
    const {
      searchQuery = "",
      offset = 0,
      limit = PAGINATION_DEFAULTS.PAGESIZE,
      append = false,
    } = payload;

    setGetDepartmentsLoading(true);
    try {
      const response = await departmentsService.fetchDepartments({
        offset,
        limit,
        searchQuery,
        sortBy: "createdAt",
        sortOrder: "ASC",
      });

      if (response?.data?.data) {
        const newRows = response.data.data.rows || [];
        const totalCount = response.data.data.count || 0;

        setDepartmentCount(totalCount);

        if (append) {
          // Deduplicate data by id
          setDepartmentData((prev: any[]) => {
            const existingIds = new Set(prev.map((item: any) => item.id));
            const uniqueNewRows = newRows.filter(
              (item: any) => !existingIds.has(item.id)
            );
            return [...prev, ...uniqueNewRows];
          });
        } else {
          setDepartmentData(newRows);
        }

        // Check if there's more data to load
        setDepartmentHasMore(offset + newRows.length < totalCount);

        return response?.data;
      }
    } catch (error: any) {
      showNotification({
        message: t("assessments.notifications.fetchDepartmentsError"),
        type: "error",
      });
      throw error;
    } finally {
      setGetDepartmentsLoading(false);
    }
  };

  return {
    departmentData,
    setDepartmentData,
    departmentCount,
    departmentHasMore,
    addDepartmentLoading,
    updateDepartmentLoading,
    deleteDepartmentLoading,
    getDepartmentsLoading,
    getDepartmentLoading,
    fetchDepartments,
  };
}
